<template>
  <!-- eslint-disable max-len -->
  <div id="app">
    <transition name="fade">
      <confirmation-modal
        v-if="showConfirmModal"
        :confirmation-type="confirmation.type"
        :title="confirmation.title"
        :description="confirmation.description"
        :button-text="confirmation.button"
        @confirm="hideStartGuideTopbar"
        @cancel="toggleConfirmModal"
      />
    </transition>
    <transition
      name="page-fade"
      mode="out-in"
    >
      <sidebar v-if="isLoginGated" />
    </transition>
    <mobile-menu v-if="isLoginGated" />
    <div class="main-container">
      <alert-bar :data="notificationData" />
      <template v-if="isLoginGated">
        <transition
          name="slide-left"
          mode="out-in"
        >
          <template v-if="showGuidePanel">
            <getting-started-panel @close="toggleGuidePanel">
            </getting-started-panel>
          </template>
          <template v-else>
            <template v-if="showGuideTopbar">
              <div class="guide-topbar">
                <div class="guide-topbar__message">
                  <strong class="mr-1">&#x1F389; Welcome! &#x1F389;</strong> Let’s get you set up!
                  To complete your onboarding, follow these simple steps.
                </div>
                <button
                  class="btn btn--blue"
                  @click="toggleGuidePanel"
                >
                  Get Started
                </button>
                <span
                  class="guide-topbar__exit"
                  @click="toggleConfirmModal"
                  v-html="closeIcon"
                />
              </div>
            </template>
          </template>
        </transition>
      </template>
      <transition
        name="page-fade"
        mode="out-in"
      >
        <router-view :class="{ 'router-view-container': isLoginGated }" />
      </transition>
      <inactivity-timeout v-if="isLoginGated"
        :idle-timeout="idleTimeout"
        :idle-warning-time="idleWarningTime"
      />
    </div>
  </div>
  <!-- eslint-enable max-len -->
</template>

<script>
import Vue from 'vue'
import VueRouter from 'vue-router'

import Sidebar from './components/sidebar.vue'
import AlertBar from './components/alertBar.vue'
import MobileMenu from './components/mobileMenu.vue'
import GettingStartedPanel from './components/gettingStartedPanel.vue'
import ConfirmationModal from '@/components/confirmationModal.vue'
import feather from 'feather-icons'
import InactivityTimeout from './components/inactivityTimeout.vue'

import tippy from 'tippy.js'
import 'tippy.js/dist/tippy.css'
import 'tippy.js/themes/light.css'

import {abilitiesPlugin} from '@casl/vue'
import {PureAbility} from '@casl/ability'

import GraphQL from '@/graphql/global.js'
import storageMixin from './mixins/storageMixin'
import {ENV} from '@/config'
import constants from './global/constants'

// Trigger event when clicked outside of an element
Vue.directive('click-outside', {
  bind(element, binding, vnode) {
    element.clickOutsideEvent = (event) => {
      if (!(element === event.target || element.contains(event.target))) {
        vnode.context[binding.expression](event)
      }
    }
    document.body.addEventListener('click', element.clickOutsideEvent)
  },
  unbind(element) {
    document.body.removeEventListener('click', element.clickOutsideEvent)
  },
})

// Focus on element when inserted into DOM
Vue.directive('focus', {
  inserted: function (el) {
    el.focus()
  },
})

const tippyDelay = 700
const tippyOptions = {
  theme: 'light',
  delay: [tippyDelay, null],
}
/*
 * Display a tooltip using Tippy.js
 * When conditional elements are created, we need to check
 * when the component is updated to add the new tippy content
 */
Vue.directive('tooltip', {
  bind(element, binding) {
    tippy(element, {
      ...binding.value,
      ...tippyOptions,
    })
  },
  update(element, binding) {
    if (element._tippy) {
      element._tippy.setContent(binding.value.content)
    }
  },
  unbind(element) {
    element._tippy.destroy()
  },
})

// Ability plugin is used to control users permissions (read, update ...)
Vue.use(abilitiesPlugin, new PureAbility([]), {
  useGlobalProperties: true,
})

Vue.use(VueRouter)
import routes from './routes/routes.js'
const router = new VueRouter({routes})
router.beforeEach((to, from, next) => {
  let canAccess = true

  // Before loading the page we check if there is an active session. If so,
  // we redirect to the users page, otherwise login as usual
  const accessToken = storageMixin.methods.getStorage(constants.storageKeys.AccessToken)
  if (to.name === 'login' && accessToken) {
    next({ name: 'users' })
  } else {
    if (to.meta.authorizationResource) {
      if (!router.app.$ability.can('view', to.meta.authorizationResource)) {
        canAccess = false
      }
    }
    next(canAccess)
  }
})

router.afterEach((to) => {
  document.title = to.meta.route_title ? to.meta.route_title : 'BioConnect Link'
  if (!to.meta.bypassLogin) {
    if (!storageMixin.methods.getStorage(constants.storageKeys.AccessToken)) {
      router.push({name: 'login'})
    }
  }
})

Vue.mixin(GraphQL)
Vue.mixin(storageMixin)

export default {
  name: 'App',
  router,
  components: {
    Sidebar,
    AlertBar,
    MobileMenu,
    GettingStartedPanel,
    ConfirmationModal,
    InactivityTimeout,
  },
  data() {
    return {
      showGuidePanel: false,
      showGuideTopbar: true,
      showConfirmModal: false,
      notificationData: {},
      confirmation: {
        type: 'friendly',
        title: 'Don\'t show again?',
        description:
          'You will still be able to access the starting guide from the help menu in the sidebar.',
        button: 'Hide Guide',
      },
      closeIcon: feather.icons.x.toSvg(),
      hideGuideEvent: new Event('hideGuide'),
      idleTimeout: 0,
      idleWarnTime: 0,
    }
  },
  computed: {
    isLoginGated() {
      return !this.$route.meta.bypassLogin
    },
  },
  created() {
    this.checkValidToken()
    this.initProductionAddons()
    this.unregisterServiceWorkers()
    this.showStartGuideTopbar()
    this.loadUserAuthorizations()
    // Show a new notification
    this.$root.$on('showNotification', (notification) => {
      this.notificationData = notification
    })
    this.$root.$on('open-guide-panel', this.toggleGuidePanel)

    this.idleTimeout = ENV.IDLE_TIMEOUT || 900
    this.idleWarningTime = ENV.IDLE_WARNING_TIME || 30
  },
  methods: {
    checkValidToken() {
      if (!this.getTokenFromStorage() && this.isLoginGated) {
        this.logout()
      }
    },
    initProductionAddons() {
      // For CrazyEgg integration
      if (this.isProductionEnvironment()) {
        const crazyEggScript = document.createElement('script')
        crazyEggScript.type = 'text/javascript'
        crazyEggScript.src = '//script.crazyegg.com/pages/scripts/0050/6003.js'
        crazyEggScript.async = 'async'
        document.head.appendChild(crazyEggScript)
      }
    },
    unregisterServiceWorkers() {
      if (navigator && navigator.serviceWorker) {
        navigator.serviceWorker
          .getRegistrations()
          .then((registrations) => {
            registrations.forEach((registration) => {
              registration.unregister()
            })
          })
          .catch((error) => {
            console.log('Service Worker registration failed: ', error)
          })
        caches
          .keys()
          .then((cacheNames) => {
            cacheNames.forEach((cacheName) => {
              caches.delete(cacheName)
            })
          })
          .catch((error) => {
            console.log('Unable to delete cache: ', error)
          })
      }
    },
    toggleGuidePanel() {
      this.showGuidePanel = !this.showGuidePanel
    },
    showStartGuideTopbar() {
      this.showGuideTopbar = !JSON.parse(localStorage.getItem('hideGuide', false))
    },
    hideStartGuideTopbar() {
      this.toggleConfirmModal()
      localStorage.setItem('hideGuide', true)
      window.dispatchEvent(this.hideGuideEvent)
      this.showGuideTopbar = !this.showGuideTopbar
    },
    toggleConfirmModal() {
      this.showConfirmModal = !this.showConfirmModal
    },
  },
}
</script>

<style lang="scss">
@import "@/scss/_main.scss";
.guide-topbar {
  @include flex-display();
  @include flex-align-items(center);
  @include flex-justify-content(space-between);
  width: calc(100% - #{$sidebar-width});
  height: 65px;
  padding: 1em;
  margin-left: $sidebar-width;
  color: darken($blue, 20%);
  box-sizing: border-box;
  background-color: lighten($blue, 30%);
  border-left: 1em solid $blue;
  box-shadow:
    0 4px 20px 0 rgba(0, 0, 0, 0.1),
    0px 2px 5px 0 rgba(0, 0, 0, 0.1);
  &__message {
    flex-grow: 1;
  }
  &__exit {
    @include flex-display();
    @include flex-align-items(center);
    @include flex-justify-content(center);
    cursor: pointer;
    width: 35px;
    height: 35px;
    margin-left: 2em;
    transition: transform 0.3s ease-in-out;
    &:hover {
      transform: scale(1.2);
    }
  }
}
</style>
